import { O, pipe } from '@mobily/ts-belt';
import { Lambda } from 'common/types';
import { useQueryClient } from 'react-query';
import { usePropertyRadioContext } from 'app/properties/components/SingleRadioProperty';
import { usePremiseRadioContext } from 'app/premises/components/SinglePremise/SingleRadioPremise';
import { usePremiseContext } from 'app/premises/components/SinglePremise/SinglePremise';
import { usePropertyContext } from '../../properties/components/SingleProperty';
import { PATCH_BUNDLE, patchBundle } from '../api/patchBundle';
import { PATCH_CABLE_ROUTE, patchCableRoute } from '../api/patchCableRoute';
import { PATCH_FIRE_BREACH, patchFireBreach } from '../api/patchFireBreach';
import { PATCH_ODF, patchODF } from '../api/patchODF';
import { PATCH_TECHNICAL_ODF, patchTechnicalODF } from '../api/patchTechnicalODF';
import { PATCH_RACK, patchRack } from '../api/patchRack';
import { PATCH_SWITCH, patchSwitch } from '../api/patchSwitch';
import { PATCH_TECHNICAL_SWITCH, patchTechnicalSwitch } from '../api/patchTechnicalSwitch';
import { PATCH_WIFIDOT, patchWifiDot } from '../api/patchWifiDot';
import { PATCH_TPOUTLET, patchTpOutlet } from '../api/patchTpOutlet';
import { PATCH_CROSS_CONNECTION_PANEL, patchCrossConnectionPanel } from '../api/patchCrossPanel';
import { PATCH_TECHNICAL_EQUIPMENT, patchTechnicalEquipment } from '../api/patchTechnicalEquipment';
import { PATCH_TRUNK, patchTrunk } from '../api/patchTrunk';
import { PATCH_BASE_STATION, patchBaseStation } from '../api/patchBaseStation';
import { PATCH_DROP_ANTENNA, patchDropAntenna } from '../api/patchDropAntenna';
import { PATCH_RADIO_UNIT, patchRadioUnit } from '../api/patchRadioUnit';
import { PATCH_INPUT_ANTENNA, patchInputAntenna } from '../api/patchInputAntenna';
import { PATCH_COUPLER, patchCoupler } from '../api/patchCoupler';
import { PATCH_COMBINER, patchCombiner } from '../api/patchCombiner';
import { PATCH_REPEATER, patchRepeater } from '../api/patchRepeater';
import { PATCH_SPLITTER, patchSplitter } from '../api/patchSplitter';
import { useOfflineMutation } from '../../../common/offline';
import { propertyRadioConnectionKey } from './useGetPropertyRadioConnections';
import { propertyConnectionKey } from './useGetPropertyConnections';
import type { GetPropertyConnectionResponse, GetPropertyRadioConnectionResponse, NodeData } from '../../connection/api';

type SuccessErrorCallbacks = {
  onSuccess: Lambda<void, void>;
  onError: Lambda<void, void>;
};

const BASIC_OBJECT_TYPES = new Set(['rack', 'fireBreach', 'cableRoute']);

// Radio domain node types (use propertyRadioConnection cache key)
const RADIO_NODE_TYPES = new Set([
  'baseStation',
  'radioUnit',
  'inputAntenna',
  'dropAntenna',
  'combiner',
  'coupler',
  'splitter',
  'repeater',
]);

// Get the correct details key for the entity type
// Some types share the same input type (e.g., technicalOdf uses odfDetails)
const getDetailsKey = (type: string): string => {
  // Map types to their actual details key when it differs from the type name
  const detailsKeyMap: Record<string, string> = {
    technicalOdf: 'odfDetails',
    technicalSwitch: 'switchDetails',
    crossPanel: 'crossConnectionPanelDetails',
  };

  if (detailsKeyMap[type]) {
    return detailsKeyMap[type];
  }

  return BASIC_OBJECT_TYPES.has(type) ? 'basicObjectDetails' : `${type}Details`;
};

const createOptimisticNodeData = (details: any, type: string) => {
  return {
    id: details.id,
    text: details.text || null,
    model: type,
    group: '', // Not inside a premise group
    cfObject: {
      text: details.text,
      location: details.location,
      images: details.photosURLs || [],
      brand: details.brand,
      model: details.model,
      installedAt: details.installedAt,
      contactType: details.contactType,
      switchType: details.switchType,
      deviceType: details.deviceType,
      ip: details.incomingIP || details.ip,
      mac: details.mac,
      serialNumber: details.serialNumber,
    },
    portCount: details.connectionsCount || details.portCount,
    leftPortCount: details.leftPortCount,
    rightPortCount: details.rightPortCount,
  };
};

const prepareNodeDetails = (details: any, propertyId: number, type: string) => {
  const nodeId = details.id;
  const nodeDetails = {
    ...details,
    id: nodeId,
    type,
  };

  // Return in the format expected by the GraphQL mutation and batch API
  // The batch API expects: { input: { propertyId, <detailsKey>: {...} } }
  // Most types use '<type>Details' (e.g., trunkDetails, odfDetails)
  // Basic object types (rack, fireBreach, cableRoute) use 'basicObjectDetails'
  const detailsKey = getDetailsKey(type);
  return {
    input: {
      propertyId,
      [detailsKey]: nodeDetails,
    },
  };
};

const getMutationName = (type: string): string => {
  const mutationNameMap: Record<string, string> = {
    odf: 'patchODF',
    technicalOdf: 'patchTechnicalODF',
    tpOutlet: 'patchTpOutlet',
    wifiDot: 'patchWifiDot',
    crossPanel: 'patchCrossConnectionPanel',
  };

  return mutationNameMap[type] || `patch${type.charAt(0).toUpperCase()}${type.slice(1)}`;
};

const usePatchNode = (
  type: string,
  apiCallFn: Lambda<number, Lambda<any, Promise<unknown>>>,
  mutationDocument: string,
  { onSuccess, onError }: SuccessErrorCallbacks,
) => {
  const { id } = usePropertyContext();
  const { id: premiseId } = usePremiseContext();
  const { id: radioId } = usePropertyRadioContext();
  const { id: propertyID } = usePremiseRadioContext();
  const queryClient = useQueryClient();

  const currentPropertyId = id || premiseId || radioId || propertyID;
  const propertyId = O.getWithDefault(currentPropertyId, 0);

  const mutationName = getMutationName(type);

  const isRadioNode = RADIO_NODE_TYPES.has(type);
  const cacheKey = isRadioNode
    ? propertyRadioConnectionKey(currentPropertyId)
    : propertyConnectionKey(currentPropertyId);

  const applyOptimisticUpdate = (optimisticData: unknown) => {
    const nodeData = optimisticData as ReturnType<typeof createOptimisticNodeData>;

    if (isRadioNode) {
      queryClient.setQueryData<GetPropertyRadioConnectionResponse | undefined>(cacheKey, (oldData) => {
        if (!oldData?.propertyById?.radioConnectionByRadioConnectionId?.serialized2) {
          return oldData;
        }
        const updatedNodeIdx =
          oldData.propertyById.radioConnectionByRadioConnectionId.serialized2.nodeDataArray.findIndex(
            (v) => v.id === nodeData.id,
          );
        if (updatedNodeIdx >= 0) {
          const oldDataCopy = structuredClone(oldData);
          (oldDataCopy.propertyById.radioConnectionByRadioConnectionId.serialized2.nodeDataArray as Array<NodeData>)[
            updatedNodeIdx
          ] = nodeData;
          return oldDataCopy;
        }
        return oldData;
      });
    } else {
      queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(cacheKey, (oldData) => {
        // Create a new node data array with the optimistic node
        const existingNodes = oldData?.propertyById?.connectionByConnectionId?.serialized2?.nodeDataArray ?? [];
        const newNodeDataArray = existingNodes.map((v) => {
          if (v.id === nodeData.id) {
            return nodeData;
          }
          return v;
        });

        // If there's no existing data structure, create one
        if (!oldData?.propertyById?.connectionByConnectionId?.serialized2) {
          return {
            propertyById: {
              ...oldData?.propertyById,
              connectionByConnectionId: {
                ...oldData?.propertyById?.connectionByConnectionId,
                serialized2: {
                  nodeDataArray: newNodeDataArray,
                  linkDataArray: oldData?.propertyById?.connectionByConnectionId?.serialized2?.linkDataArray ?? [],
                },
              },
            },
          } as GetPropertyConnectionResponse;
        }

        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            connectionByConnectionId: {
              ...oldData.propertyById.connectionByConnectionId,
              serialized2: {
                ...oldData.propertyById.connectionByConnectionId.serialized2,
                nodeDataArray: newNodeDataArray,
              },
            },
          },
        };
      });
    }
  };

  const offlineMutation = useOfflineMutation<unknown, unknown, any>({
    mutationFn: (preparedPayload: any) =>
      pipe(
        currentPropertyId,
        // The payload is already in { input: { propertyId, ...Details } } format
        // Extract the details part to pass to the API function
        O.map((propId) => {
          const detailsKey = getDetailsKey(type);
          const nodeDetails = preparedPayload.input[detailsKey];
          return apiCallFn(propId)(nodeDetails);
        }),
        O.getExn,
      ),
    mutationName,
    graphqlDocument: mutationDocument,
    entity: type,
    operationType: 'UPDATE',
    propertyId,
    invalidateKeys: [
      ['propertyConnection', currentPropertyId],
      ['propertyRadioConnection', currentPropertyId],
    ],
    // Generate optimistic data from the payload for immediate UI update
    getOptimisticData: (preparedPayload: any) => {
      const detailsKey = getDetailsKey(type);
      const nodeDetails = preparedPayload.input[detailsKey];
      return createOptimisticNodeData(nodeDetails, type);
    },
    applyOptimisticUpdate,
    onSuccess: () => {
      onSuccess();
    },
    onError: () => {
      onError();
    },
    onOfflineQueued: () => {
      // Still call onSuccess when queued offline so UI updates
      onSuccess();
    },
  });

  return {
    ...offlineMutation,
    mutate: (details: any, options?: any) => {
      const preparedPayload = prepareNodeDetails(details, propertyId, type);
      return offlineMutation.mutate(preparedPayload, options);
    },
    mutateAsync: (details: any, options?: any) => {
      const preparedPayload = prepareNodeDetails(details, propertyId, type);
      return offlineMutation.mutateAsync(preparedPayload, options);
    },
  };
};

export const usePatchBundle = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('bundle', patchBundle, PATCH_BUNDLE, callbacks);
};

export const usePatchODF = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('odf', patchODF, PATCH_ODF, callbacks);
};

export const usePatchTechnicalODF = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('technicalOdf', patchTechnicalODF, PATCH_TECHNICAL_ODF, callbacks);
};

export const usePatchSwitch = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('switch', patchSwitch, PATCH_SWITCH, callbacks);
};

export const usePatchTechnicalSwitch = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('technicalSwitch', patchTechnicalSwitch, PATCH_TECHNICAL_SWITCH, callbacks);
};

export const usePatchWifiDot = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('wifiDot', patchWifiDot, PATCH_WIFIDOT, callbacks);
};

export const usePatchTpOutlet = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('tpOutlet', patchTpOutlet, PATCH_TPOUTLET, callbacks);
};

export const usePatchCrossPanel = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('crossPanel', patchCrossConnectionPanel, PATCH_CROSS_CONNECTION_PANEL, callbacks);
};

export const usePatchTechnicalEquipment = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('technicalEquipment', patchTechnicalEquipment, PATCH_TECHNICAL_EQUIPMENT, callbacks);
};

export const usePatchTrunk = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('trunk', patchTrunk, PATCH_TRUNK, callbacks);
};

export const usePatchFireBreach = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('fireBreach', patchFireBreach, PATCH_FIRE_BREACH, callbacks);
};

export const usePatchRack = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('rack', patchRack, PATCH_RACK, callbacks);
};

export const usePatchCableRoute = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('cableRoute', patchCableRoute, PATCH_CABLE_ROUTE, callbacks);
};

export const usePatchBaseStation = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('baseStation', patchBaseStation, PATCH_BASE_STATION, callbacks);
};

export const usePatchDropAntenna = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('dropAntenna', patchDropAntenna, PATCH_DROP_ANTENNA, callbacks);
};

export const usePatchRadioUnit = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('radioUnit', patchRadioUnit, PATCH_RADIO_UNIT, callbacks);
};

export const usePatchInputAntenna = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('inputAntenna', patchInputAntenna, PATCH_INPUT_ANTENNA, callbacks);
};

export const usePatchCoupler = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('coupler', patchCoupler, PATCH_COUPLER, callbacks);
};

export const usePatchCombiner = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('combiner', patchCombiner, PATCH_COMBINER, callbacks);
};

export const usePatchRepeater = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('repeater', patchRepeater, PATCH_REPEATER, callbacks);
};

export const usePatchSplitter = (callbacks: SuccessErrorCallbacks) => {
  return usePatchNode('splitter', patchSplitter, PATCH_SPLITTER, callbacks);
};
